import { useCallback, useEffect, useMemo, useState, Fragment } from 'react';
import { Tab, Tabs, Tooltip, useTheme, Button } from '@mui/material';
import EditNoteIcon from '@mui/icons-material/EditNote';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { GridFilterModel, GridSortModel } from '@mui/x-data-grid-pro';
import { Link } from 'react-router-dom';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { PageLayout } from '../../PageLayout';
import { UpstackDataGrid } from '../../Compounds/UpstackDataGrid/UpstackDataGrid';
import { TabPanel } from '../../Atoms/TabPanel';
import {
  ApiAdvancedFilter,
  getSelectedTab,
  PaginatedResponse,
  updateState,
  FilterableField
} from '../../../store/GeneralStore';
import { TFU, fetchTFUs, getTallyTFUsSubject, TicketFollowUpResult } from '../../../store/TFU';
import { DefaultTFUGridColumnDefs } from './TFUGridColumnDefs';
import {
  getCustomizationSubject,
  getCustomViewSubject,
  CustomView,
  PrefKey,
  UserCustomizations
} from '../../../store/UserCustomizations';
import {
  buildAdvancedFilterSet,
  getColsForTab,
  getInitialState,
  mergeAndDeduplicateFilters
} from '../../Compounds/UpstackDataGrid/helpers';
import { ComponentSpinner } from '../../Compounds/Loading/ComponentSpinner';
import { getCurrentUser } from '../../../store/User';
import { DEFAULT_TAB_SX_STYLES } from '../../../utils/styleHelpers';
import { TFUWidget } from '../../Compounds/Widgets/TFU';
import { ViewsModal } from '../../Compounds/CustomTableViews/ViewsModal';
import { NewViewModal } from '../../Compounds/CustomTableViews/NewViewModal';
import { Menu as CustomViewMenu } from '../../Compounds/CustomTableViews/Menu';
import { getFiltersFromSearchParams, clearFiltersFromSearchParams } from '../../../utils/helpers';

export interface TabProps {
  name: string;
  customView?: boolean;
  filter?: FilterableField[];
  tooltip?: string;
  disabledFilters?: string[];
}

export const TFUPage = () => {
  const [tfusLoading, setTfusLoading] = useState<boolean>(false);
  const [tfus, setTfus] = useState<PaginatedResponse<TFU>>();
  const [userCustomizations, setUserCustomizations] = useState<UserCustomizations>();
  const [selectedTab, setSelectedTab] = useState(getSelectedTab().tfus);
  const [customViews, setCustomViews] = useState<CustomView[]>([]);
  const [customViewNames, setCustomViewNames] = useState<string[]>([]);
  const [selectedCustomView, setSelectedCustomView] = useState<string>('');
  const [open, setOpen] = useState<boolean>(false);
  const [createOpen, setCreateOpen] = useState<boolean>(false);
  const [triggerSave, setTriggerSave] = useState<boolean>(false);
  const [newlyCreatedViewName, setNewlyCreatedViewName] = useState<string | null>(null);

  const theme = useTheme();
  const filterKeys = DefaultTFUGridColumnDefs()
    .filter((column) => column.filterable)
    .map((column) => column.field);

  const parsedFilters = useMemo(() => getFiltersFromSearchParams(filterKeys), [selectedTab]);

  const defaultTabs = useMemo(
    () =>
      [
        {
          name: 'My Overdue',
          filter: [
            {
              operator: 'equals',
              field: 'owner.email',
              value: getCurrentUser()?.email
            },
            {
              operator: '<',
              field: 'dueDate',
              value: new Date().toISOString()
            },
            {
              operator: 'equals',
              field: 'result',
              value: TicketFollowUpResult.PENDING
            }
          ],
          tooltip: `Owner equals ${getCurrentUser()?.email} and "Due" is today or
                    earlier and "result" is “${TicketFollowUpResult.PENDING}”`,
          disabledFilters: ['owner.email', 'dueDate', 'result']
        },
        {
          name: 'My Pending',
          filter: [
            {
              operator: 'equals',
              field: 'owner.email',
              value: getCurrentUser()?.email
            },
            {
              operator: 'equals',
              field: 'result',
              value: TicketFollowUpResult.PENDING
            }
          ],
          tooltip: `Owner equals ${getCurrentUser()?.email} and "result" is “${TicketFollowUpResult.PENDING}”`,
          disabledFilters: ['owner.email', 'result']
        },
        {
          name: 'Overdue',
          filter: [
            {
              operator: '<',
              field: 'dueDate',
              value: new Date().toISOString()
            },
            {
              operator: 'equals',
              field: 'result',
              value: TicketFollowUpResult.PENDING
            }
          ],
          tooltip: `"Due" is today or earlier and "result" is “${TicketFollowUpResult.PENDING}”`,
          disabledFilters: ['dueDate', 'result']
        },
        {
          name: 'Pending',
          filter: [
            {
              operator: 'equals',
              field: 'result',
              value: TicketFollowUpResult.PENDING
            }
          ],
          tooltip: `"Result" is “${TicketFollowUpResult.PENDING}”`,
          disabledFilters: ['result']
        },
        {
          name: 'All',
          filter: [],
          tooltip: 'Unfiltered',
          disabledFilters: []
        }
      ] as TabProps[],
    []
  );

  const allAvailableViews = useMemo(() => {
    const customViewsTabProps: TabProps[] = customViews
      .filter((view) => view.page === 'tfuGrid_')
      .map((t) => ({ name: t.name, customView: true }));

    return [...defaultTabs, ...customViewsTabProps];
  }, [defaultTabs, customViews]);

  const handleFetchTFUs = useCallback(
    async (filters?: ApiAdvancedFilter, localLoading?: boolean, forceRefresh = false) => {
      if (localLoading) setTfusLoading(true);

      let currentTabFilters: FilterableField[] = [];
      const currentSelectedView = allAvailableViews[selectedTab];

      if (currentSelectedView) {
        currentTabFilters = (currentSelectedView.filter as FilterableField[]) || [];
      }

      await fetchTFUs(
        {
          ...filters,
          filters: mergeAndDeduplicateFilters(filters?.filters || [], currentTabFilters, true)
        },
        forceRefresh
      );
      if (localLoading) setTfusLoading(false);
    },
    [allAvailableViews, selectedTab]
  );

  const handleTabChange = useCallback(
    (index: number, name: string, type: 'main' | 'custom') => {
      if (type === 'main') {
        setSelectedCustomView('');
      } else if (type === 'custom') {
        setSelectedCustomView(name);
      }

      const actualIndex = allAvailableViews.findIndex((tab) => {
        if (type === 'custom') {
          return tab.name === name && tab.customView;
        } else {
          return tab.name === name && !tab.customView;
        }
      });

      if (actualIndex !== -1) {
        updateState({
          selectedTab: {
            ...getSelectedTab(),
            tfus: actualIndex
          }
        });
        setSelectedTab(actualIndex);
      }
    },
    [allAvailableViews]
  );

  const getInitialSort = useCallback(
    (userCust: UserCustomizations) => {
      const currentTab = allAvailableViews[selectedTab];
      let pageName = currentTab?.name || '';
      if (currentTab && !currentTab.customView) {
        pageName = pageName.replace(/\s/g, '');
      }
      const currentPage = `tfuGrid_${pageName}` as PrefKey;
      const savedSort = userCust?.content?.[currentPage]?.muiConfig?.sorting?.sortModel;
      return savedSort?.length ? savedSort : ([] as GridSortModel);
    },
    [allAvailableViews, selectedTab]
  );

  const getInitialFilter = useCallback(
    (userCust: UserCustomizations) => {
      if (parsedFilters.length > 0) {
        clearFiltersFromSearchParams();
        return { items: parsedFilters, logicOperator: 'and' } as GridFilterModel;
      }
      const currentTab = allAvailableViews[selectedTab];
      let pageName = currentTab?.name || '';
      if (currentTab && !currentTab.customView) {
        pageName = pageName.replace(/\s/g, '');
      }
      const currentPage = `tfuGrid_${pageName}` as PrefKey;
      const savedFilter = userCust?.content?.[currentPage]?.muiConfig?.filter?.filterModel;
      return savedFilter || ({} as GridFilterModel);
    },
    [allAvailableViews, selectedTab]
  );

  const dataFetch = useCallback(() => {
    if (userCustomizations && allAvailableViews[selectedTab]) {
      const initialSortModel = getInitialSort(userCustomizations);
      const initialFilterModel = getInitialFilter(userCustomizations);
      handleFetchTFUs(
        {
          ...buildAdvancedFilterSet(initialFilterModel || ({} as GridFilterModel), filterKeys),
          orders: initialSortModel
            ? initialSortModel.map((sort) => ({ sort: sort.field, direction: sort.sort }))
            : undefined
        },
        true,
        true
      );
    }
  }, [selectedTab, userCustomizations, allAvailableViews, getInitialSort, getInitialFilter, filterKeys]);

  const handleNewViewCreated = useCallback((viewName: string) => {
    setNewlyCreatedViewName(viewName);
  }, []);

  useEffect(() => {
    const tfuSub = getTallyTFUsSubject().subscribe((a) => setTfus(a));
    const userCustSub = getCustomizationSubject().subscribe((userCust) => setUserCustomizations(userCust));
    const customViewsSub = getCustomViewSubject().subscribe((custViews) => setCustomViews(custViews));

    return () => {
      if (tfuSub) tfuSub.unsubscribe();
      if (userCustSub) userCustSub.unsubscribe();
      if (customViewsSub) customViewsSub.unsubscribe();
    };
  }, []);

  useEffect(() => {
    setCustomViewNames(customViews.filter((view) => view.page === 'tfuGrid_').map((v) => v.name));
  }, [customViews]);

  useEffect(() => {
    if (allAvailableViews.length > 0 && selectedTab !== undefined && selectedTab >= 0) {
      const initialSelectedView = allAvailableViews[selectedTab];
      if (initialSelectedView) {
        if (initialSelectedView.customView) {
          setSelectedCustomView(initialSelectedView.name);
        } else {
          setSelectedCustomView('');
        }
      }
    }
  }, [allAvailableViews, selectedTab]);

  useEffect(() => {
    if (newlyCreatedViewName) {
      const newViewIndex = allAvailableViews.findIndex((tab) => tab.name === newlyCreatedViewName && tab.customView);
      if (newViewIndex !== -1) {
        handleTabChange(newViewIndex, newlyCreatedViewName, 'custom');
        setNewlyCreatedViewName(null);
      }
    }
  }, [newlyCreatedViewName, allAvailableViews, handleTabChange]);

  useEffect(() => {
    if (userCustomizations && allAvailableViews.length > 0) {
      dataFetch();
    }
  }, [selectedTab, userCustomizations, allAvailableViews]);

  useEffect(() => {
    if (selectedCustomView && !customViewNames.includes(selectedCustomView)) {
      setSelectedCustomView('');
      const allTabIndex = allAvailableViews.findIndex((tab) => tab.name === 'All' && !tab.customView);
      handleTabChange(allTabIndex !== -1 ? allTabIndex : 0, 'All', 'main');
    }
  }, [selectedCustomView, customViewNames, allAvailableViews, handleTabChange]);

  const handleClose = () => {
    setOpen(false);
  };

  const handleNewTabModalOpen = () => {
    setCreateOpen(true);
  };

  const handleCustomViewMenuChange = useCallback(
    (name: string) => {
      const index = allAvailableViews.findIndex((tab) => tab.name === name && tab.customView);
      if (index !== -1) {
        handleTabChange(index, name, 'custom');
      }
    },
    [allAvailableViews, handleTabChange]
  );

  return (
    <>
      <div className="mt-8 ml-6 -mb-4">
        <Link
          className="text-indigo inline-block relative"
          title="Fullscreen"
          to="/ticket">
          <div className="flex items-center text-small">
            <ArrowBackIosNewIcon sx={{ fontSize: 14 }} />
            &nbsp; Tickets
          </div>
        </Link>
      </div>
      <PageLayout
        dataTestId="ticket-follow-ups-page"
        pageTitle="Follow-Ups">
        <Fragment>
          <TFUWidget />
          <Tabs
            sx={DEFAULT_TAB_SX_STYLES}
            value={selectedTab}
            variant="scrollable"
            scrollButtons="auto"
            onChange={(_e, index) => {
              const currentTab = allAvailableViews[index];
              if (currentTab) {
                if (currentTab.customView) {
                  handleTabChange(index, currentTab.name, 'custom');
                } else {
                  handleTabChange(index, currentTab.name, 'main');
                }
              }
            }}
            slotProps={{
              indicator: {
                sx: {
                  backgroundColor: theme.palette.primary.main,
                  height: allAvailableViews[selectedTab]?.customView ? 0 : 2,
                  borderRadius: 1
                }
              }
            }}>
            {defaultTabs.map((tab, index) => (
              <Tab
                sx={{ textTransform: 'none', fontSize: '.8rem', color: 'grey6' }}
                key={index}
                label={
                  <Tooltip
                    title={<span style={{ whiteSpace: 'pre-line' }}>{tab.tooltip}</span>}
                    slotProps={{
                      tooltip: {
                        sx: {
                          maxWidth: 'none',
                          width: 'auto'
                        }
                      }
                    }}>
                    <span>{tab.name}</span>
                  </Tooltip>
                }
              />
            ))}
          </Tabs>
          <div className="-mt-12 float-right inline-flex pr-4">
            <div className={`flex ${selectedCustomView ? 'border-b-2 border-indigo' : ''}`}>
              <div
                key="custom-views"
                className="self-center">
                <CustomViewMenu
                  setSelectedView={setSelectedCustomView}
                  selectedView={selectedCustomView}
                  viewOptions={customViewNames}
                  handleViewChange={handleCustomViewMenuChange}
                  defaultTitle="My Views"
                />
              </div>
            </div>
            <div className="ml-3 self-center">
              <Button
                onClick={() => setTriggerSave(true)}
                startIcon={<AddCircleIcon />}
                sx={{
                  fontSize: '.8rem',
                  textTransform: 'none'
                }}>
                Save As View
              </Button>
            </div>
            <div
              className="ml-3 self-center"
              key="add-custom-view">
              <Button
                onClick={() => setOpen(true)}
                startIcon={<EditNoteIcon />}
                sx={{
                  fontSize: '.8rem',
                  textTransform: 'none'
                }}>
                Edit Views
              </Button>
            </div>
          </div>
          {allAvailableViews.map((tab, index) => {
            let tabKeyName = tab.name || '';
            if (!tab.customView) {
              tabKeyName = tabKeyName.replace(/\s/g, '');
            }
            const tabKey = `tfuGrid_${tabKeyName}` as PrefKey;
            const colsForTab = getColsForTab(DefaultTFUGridColumnDefs(), tab.disabledFilters ?? []);
            const gridSettingsForTab = userCustomizations?.content?.[tabKey];
            let initialState;
            if (gridSettingsForTab) {
              initialState = getInitialState(gridSettingsForTab, colsForTab, tabKey);
              const initialFilterModel = getInitialFilter(userCustomizations);
              if (initialState && initialFilterModel) {
                if (initialState.filter) {
                  initialState.filter.filterModel = initialFilterModel;
                } else {
                  initialState.filter = { filterModel: initialFilterModel };
                }
              }
            }

            return (
              <Fragment key={index}>
                {!initialState ||
                  (tfusLoading && (
                    <div className="flex h-[45rem] m-auto">
                      <ComponentSpinner label="Loading data..." />
                    </div>
                  ))}
                {initialState && !tfusLoading && (
                  <TabPanel
                    key={`${index}`}
                    index={index}
                    value={selectedTab}>
                    <UpstackDataGrid
                      advancedFiltering={true}
                      apiFilter={true}
                      columns={colsForTab}
                      fetchFunction={handleFetchTFUs}
                      filterKeys={filterKeys}
                      gridSettings={gridSettingsForTab}
                      handleNewTabModalOpen={handleNewTabModalOpen}
                      initialState={initialState}
                      page={tabKey}
                      rows={tfus?.data || []}
                      rowCount={tfus?.totalCount}
                      showSearch={false}
                      title="Follow-Ups"
                      triggerSave={triggerSave}
                      handleSaveComplete={() => setTriggerSave(false)}
                    />
                  </TabPanel>
                )}
              </Fragment>
            );
          })}
        </Fragment>
        <ViewsModal
          open={open}
          handleClose={handleClose}
          type="tfu"
        />
        <NewViewModal
          onViewCreated={handleNewViewCreated}
          open={createOpen}
          setOpen={setCreateOpen}
          sourceTab={allAvailableViews[selectedTab]?.name || 'All'}
          sourceTabFilter={allAvailableViews[selectedTab]?.filter}
          type="tfu"
        />
      </PageLayout>
    </>
  );
};
